import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip
} from '@mui/material';
import { 
  Cookie as CookieIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  ContactSupport as ContactSupportIcon
} from '@mui/icons-material';

const CookiePolicyPage = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Stack spacing={4}>
          {/* Header */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h1" gutterBottom>
              Cookie Policy
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Last updated: {new Date().toLocaleDateString()}
            </Typography>
          </Box>

          <Divider />

          {/* Introduction */}
          <Box>
            <Typography variant="h5" gutterBottom>
              What Are Cookies?
            </Typography>
            <Typography variant="body1" paragraph>
              Cookies are small text files that are placed on your device when you visit our website. 
              They help us provide you with a better experience and allow us to improve our services.
            </Typography>
          </Box>

          {/* How We Use Cookies */}
          <Box>
            <Typography variant="h5" gutterBottom>
              How We Use Cookies
            </Typography>
            <Typography variant="body1" paragraph>
              We use cookies for several purposes:
            </Typography>
            
            <List>
              <ListItem>
                <ListItemIcon>
                  <CookieIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Essential Functionality"
                  secondary="Cookies necessary for the website to function properly"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CookieIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="User Experience"
                  secondary="Remembering your preferences and settings"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CookieIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Analytics"
                  secondary="Understanding how visitors use our website"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CookieIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Security"
                  secondary="Protecting against fraud and ensuring secure access"
                />
              </ListItem>
            </List>
          </Box>

          {/* Types of Cookies */}
          <Box>
            <Typography variant="h5" gutterBottom>
              Types of Cookies We Use
            </Typography>
            
            <Stack spacing={3}>
              {/* Necessary Cookies */}
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <SecurityIcon color="success" />
                  <Typography variant="h6">
                    Necessary Cookies
                  </Typography>
                  <Chip label="Always Active" color="success" size="small" />
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  These cookies are essential for the website to function properly. They cannot be disabled 
                  and are necessary for basic functionality like user authentication and security.
                </Typography>
                <Typography variant="body2">
                  <strong>Examples:</strong> Session management, security tokens, authentication
                </Typography>
              </Paper>

              {/* Analytics Cookies */}
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <InfoIcon color="info" />
                  <Typography variant="h6">
                    Analytics Cookies
                  </Typography>
                  <Chip label="Optional" color="info" size="small" />
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  These cookies help us understand how visitors interact with our website by collecting 
                  and reporting information anonymously.
                </Typography>
                <Typography variant="body2">
                  <strong>Examples:</strong> Google Analytics, page views, user behavior
                </Typography>
              </Paper>

              {/* Functional Cookies */}
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <SettingsIcon color="primary" />
                  <Typography variant="h6">
                    Functional Cookies
                  </Typography>
                  <Chip label="Optional" color="primary" size="small" />
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  These cookies enable enhanced functionality and personalization, such as remembering 
                  your preferences and settings.
                </Typography>
                <Typography variant="body2">
                  <strong>Examples:</strong> Language preferences, theme settings, form data
                </Typography>
              </Paper>

              {/* Marketing Cookies */}
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <InfoIcon color="secondary" />
                  <Typography variant="h6">
                    Marketing Cookies
                  </Typography>
                  <Chip label="Optional" color="secondary" size="small" />
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  These cookies are used to deliver relevant advertisements and track campaign effectiveness.
                </Typography>
                <Typography variant="body2">
                  <strong>Examples:</strong> Ad targeting, campaign tracking, social media integration
                </Typography>
              </Paper>
            </Stack>
          </Box>

          {/* Third-Party Cookies */}
          <Box>
            <Typography variant="h5" gutterBottom>
              Third-Party Cookies
            </Typography>
            <Typography variant="body1" paragraph>
              Some cookies are placed by third-party services that appear on our pages:
            </Typography>
            
            <List>
              <ListItem>
                <ListItemText 
                  primary="Google Analytics"
                  secondary="Website analytics and performance monitoring"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Social Media Platforms"
                  secondary="Integration with social media features"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Payment Processors"
                  secondary="Secure payment processing services"
                />
              </ListItem>
            </List>
          </Box>

          {/* Managing Cookies */}
          <Box>
            <Typography variant="h5" gutterBottom>
              Managing Your Cookie Preferences
            </Typography>
            <Typography variant="body1" paragraph>
              You can control and manage cookies in several ways:
            </Typography>
            
            <List>
              <ListItem>
                <ListItemText 
                  primary="Browser Settings"
                  secondary="Most browsers allow you to control cookies through settings"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Our Consent Banner"
                  secondary="Use our cookie consent banner to manage preferences"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Third-Party Opt-Outs"
                  secondary="Opt out of specific third-party services"
                />
              </ListItem>
            </List>
          </Box>

          {/* Browser Instructions */}
          <Box>
            <Typography variant="h5" gutterBottom>
              Browser-Specific Instructions
            </Typography>
            
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                  Chrome
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Settings → Privacy and security → Cookies and other site data
                </Typography>
              </Paper>
              
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                  Firefox
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Options → Privacy & Security → Cookies and Site Data
                </Typography>
              </Paper>
              
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                  Safari
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Preferences → Privacy → Manage Website Data
                </Typography>
              </Paper>
              
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                  Edge
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Settings → Cookies and site permissions → Cookies and site data
                </Typography>
              </Paper>
            </Stack>
          </Box>

          {/* Impact of Disabling */}
          <Box>
            <Typography variant="h5" gutterBottom>
              Impact of Disabling Cookies
            </Typography>
            <Typography variant="body1" paragraph>
              While you can disable cookies, please note that:
            </Typography>
            
            <List>
              <ListItem>
                <ListItemText 
                  primary="Essential Functions"
                  secondary="Some features may not work properly without necessary cookies"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="User Experience"
                  secondary="You may need to re-enter information on each visit"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Personalization"
                  secondary="We won't be able to remember your preferences"
                />
              </ListItem>
            </List>
          </Box>

          {/* Updates to Policy */}
          <Box>
            <Typography variant="h5" gutterBottom>
              Updates to This Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this Cookie Policy from time to time to reflect changes in our practices 
              or for other operational, legal, or regulatory reasons.
            </Typography>
            <Typography variant="body1">
              We will notify you of any material changes by posting the updated policy on this page 
              and updating the "Last updated" date.
            </Typography>
          </Box>

          {/* Contact Information */}
          <Box>
            <Typography variant="h5" gutterBottom>
              Contact Us
            </Typography>
            <Typography variant="body1" paragraph>
              If you have questions about our use of cookies or this Cookie Policy, please contact us:
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ContactSupportIcon color="primary" />
              <Typography variant="body1">
                Email: privacy@liferunbooks.com
              </Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary">
              We will respond to your inquiry within 30 days.
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Container>
  );
};

export default CookiePolicyPage;